import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../css/App.css';
import axios from './axiosSetup';
import API_BASE from './apiBase';
import { styles } from './styles';
import { ArrowLeft, University, UserCircle, FileText, Gavel, Hotel, Factory, Shield, Eye, Edit, TrendingUp, BarChart3, PieChart, Activity, X, Tag, Clock, CheckCircle, AlertTriangle, History, ChevronDown, ChevronUp, Languages, Globe, Lock, Mail, Cookie, FileCheck, HeartPulse } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from 'sweetalert2';
import { getTemplatesBySector, getTemplateById } from '../data/mockConsentData';
import formatDisplayLabel from './formatDisplayLabel';
import { compareConsentVersions, getConsentVersionUpdatePlan } from './consentVersionUtils';

const ICON_OPTIONS = [
  { name: 'Shield', label: '🛡️ Shield (Security & Protection)', component: Shield },
  { name: 'FileText', label: '📄 Document (File & Text)', component: FileText },
  { name: 'FileCheck', label: '📋 Checklist (Compliance)', component: FileCheck },
  { name: 'UserCircle', label: '👤 User Profile (Personal Data)', component: UserCircle },
  { name: 'University', label: '🏛️ Bank / Financial', component: University },
  { name: 'HeartPulse', label: '🩺 Healthcare / Patient', component: HeartPulse },
  { name: 'Gavel', label: '⚖️ Legal / Terms', component: Gavel },
  { name: 'Lock', label: '🔒 Security Lock', component: Lock },
  { name: 'Globe', label: '🌐 Web / Online Consent', component: Globe },
  { name: 'Cookie', label: '🍪 Cookie & Tracking', component: Cookie },
  { name: 'Hotel', label: '🏨 Hotel / Guest', component: Hotel },
  { name: 'Factory', label: '🏭 Industry / Manufacturing', component: Factory },
  { name: 'Mail', label: '✉️ Email & Communication', component: Mail },
];

const CONSENT_TYPES = [
  { key: 'bank', label: 'Bank Consent', description: 'Consent for banking data and transactions.', icon: University, color: '#0070f3', usage: 45 },
  { key: 'patient', label: 'Patient Consent', description: 'Consent for patient health data.', icon: UserCircle, color: '#00b894', usage: 78 },
  { key: 'insurance', label: 'Insurance Consent', description: 'Consent for insurance data and claims.', icon: FileText, color: '#fdcb6e', usage: 62 },
  { key: 'general', label: 'General Consent', description: 'General purpose consent form.', icon: Shield, color: '#6c5ce7', usage: 90 },
  { key: 'legal', label: 'Legal Consent', description: 'Consent for legal agreements.', icon: Gavel, color: '#d63031', usage: 34 },
  { key: 'hotel_visitor', label: 'Hotel Visitor Consent', description: 'Consent for hotel guest/visitor data.', icon: Hotel, color: '#0984e3', usage: 56 },
  { key: 'manufacturing_visitor', label: 'Manufacturing Visitor Consent', description: 'Consent for manufacturing site visitor data.', icon: Factory, color: '#636e72', usage: 28 },
];
const CONSENT_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' }, { value: 'hi', label: 'Hindi' },
  { value: 'gu', label: 'Gujarati' }, { value: 'mr', label: 'Marathi' },
  { value: 'bn', label: 'Bengali' }, { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' }, { value: 'kn', label: 'Kannada' },
  { value: 'ml', label: 'Malayalam' }, { value: 'pa', label: 'Punjabi' },
  { value: 'or', label: 'Odia' }, { value: 'ur', label: 'Urdu' },
];
const LOCALIZED_MAIN_CONSENT_TYPES = {
  general: { hi: 'सामान्य सहमति', gu: 'સામાન્ય સંમતિ', mr: 'सामान्य संमती', bn: 'সাধারণ সম্মতি', ta: 'பொதுவான சம்மதம்', te: 'సాధారణ సమ్మతి', kn: 'ಸಾಮಾನ್ಯ ಸಮ್ಮತಿ', ml: 'പൊതുവായ സമ്മതം', pa: 'ਆਮ ਸਹਿਮਤੀ', or: 'ସାଧାରଣ ସମ୍ମତି', ur: 'عام رضامندی' },
  bank: { hi: 'बैंक सहमति', gu: 'બેંક સંમતિ', mr: 'बँक संमती', bn: 'ব্যাংক সম্মতি', ta: 'வங்கி சம்மதம்', te: 'బ్యాంక్ సమ్మతి', kn: 'ಬ್ಯಾಂಕ್ ಸಮ್ಮತಿ', ml: 'ബാങ്ക് സമ്മതം', pa: 'ਬੈਂਕ ਸਹਿਮਤੀ', or: 'ବ୍ୟାଙ୍କ ସମ୍ମତି', ur: 'بینک رضامندی' },
  patient: { hi: 'रोगी सहमति', gu: 'દર્દીની સંમતિ', mr: 'रुग्ण संमती', bn: 'রোগীর সম্মতি', ta: 'நோயாளர் சம்மதம்', te: 'రోగి సమ్మతి', kn: 'ರೋಗಿಯ ಸಮ್ಮತಿ', ml: 'രോഗിയുടെ സമ്മതം', pa: 'ਮਰੀਜ਼ ਦੀ ਸਹਿਮਤੀ', or: 'ରୋଗୀ ସମ୍ମତି', ur: 'مریض کی رضامندی' },
  insurance: { hi: 'बीमा सहमति', gu: 'વીમા સંમતિ', mr: 'विमा संमती', bn: 'বীমা সম্মতি', ta: 'காப்பீட்டு சம்மதம்', te: 'బీమా సమ్మతి', kn: 'ವಿಮೆ ಸಮ್ಮತಿ', ml: 'ഇൻഷുറൻസ് സമ്മതം', pa: 'ਬੀਮਾ ਸਹਿਮਤੀ', or: 'ବୀମା ସମ୍ମତି', ur: 'انشورنس رضامندی' },
};
const initialContent = {};
CONSENT_TYPES.forEach(type => {
  initialContent[type.key] = `<h3>${type.label}</h3><p>This is the default consent content for ${type.label}. You can edit this content using the editor.</p>`;
});
export { CONSENT_TYPES, initialContent, ICON_OPTIONS };

// ─────────────────────────────────────────────────────────────────────────────
// VERSION MANAGEMENT HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const HISTORY_STORAGE_KEY = 'consent_version_history';
const loadVersionHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};
const saveVersionHistory = (history) => {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch (e) { }
};
const formatTs = (ts) => {
  try {
    return new Date(ts).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return String(ts);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// CONSENT PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
/* Page Guide modal */
function GuideModal({ steps, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2500,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 16px', overflowY: 'auto',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620,
        border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>📖 Page Guide</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#94a3b8', fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '18px 20px', maxHeight: '75vh', overflowY: 'auto' }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, padding: '12px 0',
              borderBottom: i < steps.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              <div style={{
                flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{s.title}</div>
                <div style={{ fontSize: 12.5, color: '#4b5563', lineHeight: 1.5 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function ConsentPage(props) {
  const [remoteTypes, setRemoteTypes] = useState([]);
  const [remoteTypesLoaded, setRemoteTypesLoaded] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [modalEditType, setModalEditType] = useState('');
  const [modalEditIconName, setModalEditIconName] = useState('Shield');
  const [modalEditColor, setModalEditColor] = useState('#2563eb');
  const [content, setContent] = useState({ ...initialContent });
  const [editorValue, setEditorValue] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newConsentType, setNewConsentType] = useState('');
  const [newConsentContent, setNewConsentContent] = useState('');
  const [newConsentLanguage, setNewConsentLanguage] = useState('en');
  const [newConsentCustomLabel, setNewConsentCustomLabel] = useState('');
  const [newConsentColor, setNewConsentColor] = useState('#2563eb');
  const [newConsentIconName, setNewConsentIconName] = useState('Shield');
  const [newConsentNotified, setNewConsentNotified] = useState(0);
  const [newConsentVersionForEdit, setNewConsentVersionForEdit] = useState('');
  const [customTypeMeta, setCustomTypeMeta] = useState({});
  const [showOnlyCustom, setShowOnlyCustom] = useState(false);
  // Version field state for create consent form
  const [newConsentVersion, setNewConsentVersion] = useState('');
  // ─── Version Management State ─────────────────────────────────────────────
  const [showVersionModal, setShowVersionModal] = useState(null);
  const [versionModalStep, setVersionModalStep] = useState('view');
  const [pendingNewVersion, setPendingNewVersion] = useState('');
  const [versionRejecting, setVersionRejecting] = useState(false);
  const [versionHistory, setVersionHistory] = useState(() => loadVersionHistory());
  const [selectedVersionEntry, setSelectedVersionEntry] = useState(null);
  const [historyOpen, setHistoryOpen] = useState({});
  const [tenantSector, setTenantSector] = useState(null);
  const [tourActive, setTourActive] = useState(false);
  const [cardTooltip, setCardTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [guideOpen, setGuideOpen] = useState(false);
  const GUIDE_STEPS = [
    { title: 'Navigation', body: 'Use the manager navigation to move between consent forms and the rest of your workspace.', target: '.tour-target-manager-consent-nav' },
    { title: 'Create consent', body: 'Use the Create Consent button to start a new consent form from the top of the page.', target: '.tour-target-create-consent-btn' },
    { title: 'Consent forms', body: 'Use this page to review and update the consent forms used by your team.', target: '.tour-target-consent-card' },
    { title: 'Edit and preview', body: 'Use the edit action on a consent card to open the editor and preview the content before saving.', target: '.tour-target-edit-action' },
    { title: 'Version management', body: 'Use the version badge on each card to manage the consent form version and status.', target: '.tour-target-version-badge' },
    { title: 'Save and publish', body: 'After editing, use the editor actions to save and publish the updated consent form.', target: '.tour-target-edit-action' },
  ];

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const body = document.body;
    if (!body) return undefined;
    const syncTourState = () => setTourActive(body.classList.contains('tour-active'));
    syncTourState();
    const observer = new MutationObserver(syncTourState);
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const sanitize = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html || '', 'text/html');
      const allowed = new Set(['B', 'I', 'U', 'STRONG', 'EM', 'SPAN', 'P', 'BR', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
      const walk = (node) => {
        const children = Array.from(node.childNodes);
        for (const child of children) {
          if (child.nodeType === Node.ELEMENT_NODE) {
            if (!allowed.has(child.tagName)) {
              const frag = document.createDocumentFragment();
              while (child.firstChild) frag.appendChild(child.firstChild);
              child.replaceWith(frag);
            } else {
              const style = child.getAttribute('style');
              if (style) {
                const safe = style.split(';').map(s => s.trim()).filter(s => {
                  if (!s) return false;
                  const [k, v] = s.split(':').map(t => t.trim());
                  if (!k || !v) return false;
                  if (k.toLowerCase() !== 'color') return false;
                  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v) || /^rgb\(/i.test(v) || /^rgba\(/i.test(v);
                }).join(';');
                if (safe) child.setAttribute('style', safe);
                else child.removeAttribute('style');
              }
              walk(child);
            }
          } else if (child.nodeType === Node.TEXT_NODE) {
            // leave text nodes
          } else {
            child.remove();
          }
        }
      };
      walk(doc.body);
      return doc.body.innerHTML;
    } catch (e) {
      return '';
    }
  };

  const normalizeKey = (k) => {
    try {
      if (!k && k !== 0) return '';
      return String(k).trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    } catch (e) {
      return String(k || '').toLowerCase();
    }
  };

  const canonicalConsentType = (value) => {
    const normalized = normalizeKey(value);
    if (!normalized) return '';
    const builtIn = CONSENT_TYPES.find(type => {
      const key = normalizeKey(type.key);
      const label = normalizeKey(type.label);
      return normalized === key || normalized === label || normalized === `${key}_consent`;
    });
    return builtIn ? builtIn.key : normalized;
  };

  const stripHtml = (html) => {
    try {
      const d = document.createElement('div');
      d.innerHTML = html || '';
      return d.textContent || d.innerText || '';
    } catch (e) {
      return String(html || '');
    }
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }]
    ]
  };
  const quillFormats = ['bold', 'italic', 'underline', 'color'];

  const handleAcceptConsent = async (typeKey) => {
    const key = normalizeKey(typeKey);
    const meta = customTypeMeta?.[key] || customTypeMeta?.[typeKey] || {};
    const ver = meta.version || '1.0';
    const confirm = await Swal.fire({
      title: `Accept & Lock Version v${ver}?`,
      html: `Accepting this consent version will mark it as <strong>ACCEPTED 🔒</strong>.<br><br>Direct editing will be disabled, and any future changes will require creating a new version (V2).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Accept & Lock',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#059669',
      customClass: { popup: 'swal-popup-custom' },
    });
    if (!confirm.isConfirmed) return;

    try {
      if (tenantId) {
        const headers = {
          'X-Tenant-ID': tenantId,
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          'X-Skip-Redirect-On-401': '1',
        };
        const apiKey = process.env.REACT_APP_MANAGER_API_KEY || localStorage.getItem('manager_api_key') || '';
        const apiToken = process.env.REACT_APP_MANAGER_API_TOKEN || localStorage.getItem('manager_api_token') || '';
        if (apiKey) headers['X-API-KEY'] = apiKey;
        if (apiToken) headers['X-API-TOKEN'] = apiToken;
        await axios.post(`${API_BASE}/manager/accept-consent-version`, {
          id: meta.id || null,
          tenant_id: Number(localStorage.getItem('tenant_id')) || tenantId,
          consent_type: key,
          consent_version: String(ver).trim().replace(/^v/i, ''),
        }, { headers, skipRedirectOn401: true });
      }
      setCustomTypeMeta(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          status: 'accepted',
        }
      }));
      Swal.fire({ icon: 'success', title: 'Consent Accepted!', text: `Version v${ver} has been accepted and locked.`, timer: 2000, showConfirmButton: false, customClass: { popup: 'swal-popup-custom' } });
    } catch (e) {
      console.error('Failed to accept consent', e);
      const detail = e?.response?.data?.detail || e?.message || 'Failed to accept consent version';
      Swal.fire({
        icon: 'error',
        title: 'Accept Failed',
        text: detail,
        customClass: { popup: 'swal-popup-custom' },
      });
    }
  };

  const handleCardClick = (key, mode = 'view') => {
    setSelectedType(key);
    setError('');
    const normalizedKey = normalizeKey(key);
    const cardMeta = customTypeMeta?.[normalizedKey] || customTypeMeta?.[key] || {};
    setNewConsentVersionForEdit(cardMeta?.version || '');

    const cardStatus = String(cardMeta?.status || '').toLowerCase();
    const isAcceptedStatus = cardStatus === 'accepted';
    if (mode === 'edit' && isAcceptedStatus) {
      Swal.fire({
        title: 'Consent Version Accepted & Locked 🔒',
        html: `Version <strong>v${cardMeta?.version || '1.0'}</strong> is Accepted and locked.<br><br>Direct edits are disabled for accepted versions. To make changes, please create a new version (V2).`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Create New Version (V2)',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#2563eb',
        customClass: { popup: 'swal-popup-custom' },
      }).then(res => {
        if (res.isConfirmed) {
          handleOpenVersionModal(null, key);
        }
      });
      return;
    }

    const found = CONSENT_TYPES.find(t => normalizeKey(t.key) === normalizedKey || normalizeKey(t.label) === normalizedKey);
    const currentIconName = cardMeta.iconName || (found ? (ICON_OPTIONS.find(i => i.component === found.icon)?.name || 'Shield') : 'Shield');
    const currentColor = cardMeta.color || (found ? found.color : '#2563eb');

    setModalEditIconName(currentIconName);
    setModalEditColor(currentColor);

    // Try to load from mockConsentData first
    const mockTemplate = getTemplateById(key);
    if (mockTemplate) {
      const iconComp = ICON_OPTIONS.find(i => i.name === currentIconName)?.component || Shield;
      const initialLabel = cardMeta.label || mockTemplate.name || formatDisplayLabel(key);
      setModalType({
        key,
        label: initialLabel,
        description: mockTemplate.description,
        icon: iconComp,
        color: currentColor,
        iconName: currentIconName,
      });
      setEditorValue(mockTemplate.content);
      setModalEditType(initialLabel);
    } else {
      // Fallback to CONSENT_TYPES
      const displayLabel = cardMeta.label || (found ? found.label : (key ? formatDisplayLabel(key) : ''));
      const iconComp = ICON_OPTIONS.find(i => i.name === currentIconName)?.component || (found ? found.icon : Shield);
      setModalType({
        key,
        label: displayLabel,
        description: found ? found.description : '',
        icon: iconComp,
        color: currentColor,
        iconName: currentIconName,
        usage: found ? found.usage : 0,
      });
      const norm = normalizeKey(key);
      setEditorValue(content[norm] || content[key] || '');
      setModalEditType(displayLabel);
    }

    setModalMode(mode);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalType(null);
    setModalMode('view');
    setModalEditType('');
    setNewConsentVersionForEdit('');
  };

  const handleSave = () => {
    if (modalType) {
      const displayLabel = (modalEditType || modalType.label || modalType.key || '').trim();
      const originalKey = normalizeKey(modalType.key);
      const currentVersion = customTypeMeta?.[originalKey]?.version || null;
      const currentConsentId = customTypeMeta?.[originalKey]?.id || null;
      const editVersionValue = String(currentVersion || newConsentVersionForEdit || '').trim();
      const contentText = stripHtml(editorValue).trim();

      if (!displayLabel) {
        Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please fill in Consent Title.', customClass: { popup: 'swal-popup-custom' } });
        return;
      }
      if (!modalEditIconName) {
        Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please select an Icon Shape & Design.', customClass: { popup: 'swal-popup-custom' } });
        return;
      }
      if (!modalEditColor) {
        Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please select an Icon Color.', customClass: { popup: 'swal-popup-custom' } });
        return;
      }
      if (!editVersionValue) {
        Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please fill in Consent Version.', customClass: { popup: 'swal-popup-custom' } });
        return;
      }
      if (!contentText) {
        Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please fill in Consent Content.', customClass: { popup: 'swal-popup-custom' } });
        return;
      }

      (async () => {
        try {
          const safe = sanitize(editorValue);
          const newKey = normalizeKey(displayLabel || modalType.key) || originalKey;
          const saveKey = newKey || originalKey;
          const metaLabel = displayLabel || modalType.label || modalType.key || saveKey;
          const versionPlan = {
            shouldRejectPreviousVersion: false,
            effectiveVersion: currentVersion || editVersionValue || null,
          };
          const metaPayload = {
            label: metaLabel,
            color: modalEditColor || modalType.color || '#2563eb',
            iconName: modalEditIconName || modalType.iconName || 'Shield',
            usage: modalType.usage || 0,
            // version: customTypeMeta?.[originalKey]?.version || null,
            version: versionPlan.effectiveVersion || currentVersion || null,
          };

          if (newKey && originalKey && newKey !== originalKey) {
            try {
              const renamePayload = { tenant_id: Number(localStorage.getItem('tenant_id')) || tenantId, old_type: originalKey, new_type: newKey };
              await axios.post(`${API_BASE}/manager/update-consent-type`, renamePayload);
              setRemoteTypes(prev => (prev || []).map(t => (t === originalKey ? newKey : t)));
              setContent(prev => {
                const next = { ...prev };
                if (Object.prototype.hasOwnProperty.call(next, originalKey) && !Object.prototype.hasOwnProperty.call(next, newKey)) {
                  next[newKey] = next[originalKey];
                  delete next[originalKey];
                }
                return next;
              });
              setCustomTypeMeta(prev => {
                const next = { ...prev };
                if (next && next[originalKey]) {
                  next[newKey] = { ...(next[originalKey] || {}), ...metaPayload };
                  delete next[originalKey];
                } else {
                  next[newKey] = { ...metaPayload };
                }
                return next;
              });
              const updatedIconComp = ICON_OPTIONS.find(i => i.name === (modalEditIconName || 'Shield'))?.component || Shield;
              setModalType(prev => prev ? {
                ...prev,
                key: newKey,
                label: metaLabel,
                icon: updatedIconComp,
                color: modalEditColor || '#2563eb',
                iconName: modalEditIconName || 'Shield',
              } : prev);
            } catch (e) {
              console.warn('Failed to rename consent type before save', e);
            }
          }

          if (versionPlan.shouldRejectPreviousVersion && currentVersion && tenantId) {
            try {
              await axios.post(`${API_BASE}/manager/reject-consent-version`, {
                tenant_id: Number(localStorage.getItem('tenant_id')) || tenantId,
                consent_type: saveKey,
                consent_version: currentVersion,
              }, {
                headers: {
                  'X-Tenant-ID': tenantId,
                  'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                  'X-Skip-Redirect-On-401': '1',
                },
                skipRedirectOn401: true,
              });
            } catch (apiErr) {
              console.warn('Could not reject previous consent version during edit save:', apiErr);
            }
          }

          setVersionHistory(prev => {
            const next = { ...prev };
            const existing = Array.isArray(next[saveKey]) ? [...next[saveKey]] : [];
            const rejectedAt = new Date().toISOString();
            if (versionPlan.shouldRejectPreviousVersion && currentVersion) {
              const idx = existing.findIndex(entry => entry.version === currentVersion && !entry.rejectedAt);
              if (idx !== -1) {
                existing[idx] = { ...existing[idx], rejectedAt };
              } else {
                existing.unshift({ version: currentVersion, setAt: null, rejectedAt });
              }
            }
            if (versionPlan.effectiveVersion && versionPlan.effectiveVersion !== currentVersion) {
              const alreadyPresent = existing.find(entry => entry.version === versionPlan.effectiveVersion && !entry.rejectedAt);
              if (!alreadyPresent) {
                existing.unshift({ version: versionPlan.effectiveVersion, setAt: rejectedAt, rejectedAt: null });
              }
            }
            next[saveKey] = existing;
            saveVersionHistory(next);
            return next;
          });

          const isVersionChanged = versionPlan.effectiveVersion && versionPlan.effectiveVersion !== currentVersion;
          const nextStatus = isVersionChanged ? 'under_process' : (customTypeMeta?.[saveKey]?.status || 'under_process');
          setCustomTypeMeta(prev => ({
            ...prev,
            [saveKey]: {
              ...(prev[saveKey] || {}),
              ...metaPayload,
              label: metaLabel,
              color: modalEditColor || modalType.color || '#2563eb',
              iconName: modalEditIconName || modalType.iconName || 'Shield',
              status: nextStatus,
            },
          }));
          setRemoteTypes(prev => (prev && prev.includes(saveKey)) ? prev : [saveKey].concat(prev || []));
          const updatedIconComp = ICON_OPTIONS.find(i => i.name === (modalEditIconName || 'Shield'))?.component || Shield;
          setModalType(prev => prev ? {
            ...prev,
            key: saveKey,
            label: metaLabel,
            icon: updatedIconComp,
            color: modalEditColor || '#2563eb',
            iconName: modalEditIconName || 'Shield',
          } : prev);
          setModalEditType(metaLabel);
          setModalMode('view');
          setSelectedType(saveKey);
          setNewConsentVersionForEdit('');
          try {
            const metadataObj = {
              iconName: modalEditIconName || modalType.iconName || 'Shield',
              color: modalEditColor || modalType.color || '#2563eb',
              label: metaLabel
            };
            const payload = {
              id: currentConsentId,
              tenant_id: Number(localStorage.getItem('tenant_id')) || tenantId,
              consent_type: saveKey,
              consent_message: safe,
              consent_version: editVersionValue,
              consent_metadata: JSON.stringify(metadataObj),
              color: modalEditColor || modalType.color || '#2563eb',
            };
            const endpoint = currentConsentId ? '/manager/update-new-consent' : '/manager/new-consent';
            const res = await axios.post(`${API_BASE}${endpoint}`, payload, { headers: { 'X-Tenant-ID': tenantId } });
            if (!(res && res.data && res.data.success)) {
              const detail = res && res.data ? (res.data.detail || JSON.stringify(res.data)) : 'Unknown response';
              Swal.fire({ icon: 'error', title: 'Save Failed', text: 'Failed saving consent template: ' + detail, customClass: { popup: 'swal-popup-custom' } });
            } else {
              setContent(prev => ({ ...prev, [saveKey]: safe }));
              await refreshConsentData(payload.tenant_id);
              try {
                const detail = { consent_type: payload.consent_type, consent_message: safe, tenant_id: payload.tenant_id, consent_version: currentVersion || null };
                window.dispatchEvent(new CustomEvent('consentTemplateUpdated', { detail }));
              } catch (e) { }
            }
          } catch (e) {
            console.warn('Failed to persist edited consent template', e);
            try {
              const msg = e?.response?.data?.detail || e?.message || 'Save failed';
              Swal.fire({ icon: 'error', title: 'Save Failed', text: 'Failed to save consent template: ' + msg, customClass: { popup: 'swal-popup-custom' } });
            } catch (ex) { }
          }
        } catch (e) {
          console.warn('Error during save flow', e);
        }
      })();
    }
  };

  const handleDeleteConsent = async () => {
    if (!modalType) return;
    const confirm = await Swal.fire({
      title: 'Delete consent type?',
      html: `Delete consent type "${modalType.label || modalType.key}"? This will remove stored templates.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      buttonsStyling: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });
    if (!confirm.isConfirmed) return;
    try {
      const delKey = normalizeKey(modalType.key);
      await axios.delete(`${API_BASE}/manager/new-consent`, { params: { tenant_id: Number(localStorage.getItem('tenant_id')) || tenantId, consent_type: delKey } });
      setRemoteTypes(prev => (prev || []).filter(t => t !== delKey));
      setContent(prev => {
        const next = { ...prev };
        if (Object.prototype.hasOwnProperty.call(next, delKey)) delete next[delKey];
        return next;
      });
      setCustomTypeMeta(prev => {
        const next = { ...prev };
        if (next && next[delKey]) delete next[delKey];
        return next;
      });
      setModalOpen(false);
      setModalType(null);
      await refreshConsentData(tenantId);
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Consent type deleted', customClass: { popup: 'swal-popup-custom' } });
    } catch (e) {
      console.error('Failed to delete consent type', e);
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Failed to delete consent type', customClass: { popup: 'swal-popup-custom' } });
    }
  };

  const handleSubmit = () => {
    if (!selectedType) {
      setError('Please select a consent type');
      return;
    }
    setError('');
    (async () => {
      try {
        if (!tenantId) {
          Swal.fire({ icon: 'warning', title: 'Missing Tenant', text: 'Cannot create consent: tenant_id not set.', customClass: { popup: 'swal-popup-custom' } });
          return;
        }
        const payload = {
          tenant_id: tenantId,
          consent_type: normalizeKey(selectedType),
          consent_message: content[normalizeKey(selectedType)] || content[selectedType],
          consent_metadata: '',
        };
        const res = await axios.post(`${API_BASE}/manager/new-consent`, payload, { headers: { 'X-Tenant-ID': tenantId } });
        const notified = res.data?.notified || [];
        await refreshConsentData(tenantId);
        Swal.fire({ icon: 'success', title: 'Consent Created', html: `<div style="text-align:left">Consent created successfully.<br><br><strong>Notified:</strong> ${notified.join(', ') || 'none'}</div>`, customClass: { popup: 'swal-popup-custom' } });
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: 'Create Failed', text: 'Failed to create consent. See console for details.', customClass: { popup: 'swal-popup-custom' } });
      }
    })();
  };

  const handleCreateConsent = () => {
    setNewConsentType('custom');
    setNewConsentCustomLabel('');
    setNewConsentContent('');
    setNewConsentLanguage('en');
    setNewConsentVersion('');
    setShowOnlyCustom(true);
    setShowCreateForm(true);
  };

  const refreshConsentData = useCallback(async (activeTenantId = tenantId) => {
    try {
      const resolvedTenant = activeTenantId || Number(localStorage.getItem('tenant_id')) || null;
      const url = resolvedTenant
        ? `${API_BASE}/manager/new-consents?tenant_id=${resolvedTenant}&include_inactive=true&limit=10000`
        : `${API_BASE}/manager/new-consents?include_inactive=true&limit=10000`;
      const res = await axios.get(url);
      const rows = res.data?.data || [];
      const types = [];
      const contentUpdate = {};
      const metaUpdate = {};
      const serverHistory = {};
      for (const r of rows) {
        const raw = r.consent_type || r.type || r.consent_type_name || '';
        // const ct = normalizeKey(raw || '');
        const ct = canonicalConsentType(raw || '');
        if (!ct) continue;

        let metadata = {};
        try {
          const rawMeta = r.consent_metadata || r.data || {};
          metadata = typeof rawMeta === 'string' ? JSON.parse(rawMeta) : (rawMeta && typeof rawMeta === 'object' ? rawMeta : {});
        } catch (e) {
          metadata = {};
        }
        const isMultilingualVariant = Boolean(
          metadata.kind === 'multilingual_consent'
          || metadata.is_multilingual_variant
          || metadata.parent_consent_type
          || r.kind === 'multilingual_consent'
          || r.is_multilingual_variant
        );
        if (isMultilingualVariant) continue;

        const serverVersion = r.consent_version || r.version || metadata.version || null;
        const rowStatus = String(r.status || 'under_process').toLowerCase();
        if (serverVersion) {
          if (!serverHistory[ct]) serverHistory[ct] = [];
          serverHistory[ct].push({
            rowId: r.id,
            version: String(serverVersion),
            setAt: r.created_at || null,
            rejectedAt: r.rejected_at || (['inactive', 'rejected'].includes(rowStatus) ? r.created_at : null),
            status: r.status || 'active',
            rejectionReason: r.rejection_reason || r.reject_reason || null,
            consentMessage: r.consent_message || r.description || r.message || '',
          });
        }
        if (['inactive', 'rejected'].includes(rowStatus)) continue;
        if (!types.includes(ct)) types.push(ct);
        const normalizedRowStatus = rowStatus === 'active' ? 'under_process' : rowStatus;
        const existingStatus = String(metaUpdate[ct]?.status || '').toLowerCase();
        const existingVersion = metaUpdate[ct]?.version || null;
        const versionComparison = existingVersion && serverVersion
          ? compareConsentVersions(String(serverVersion), String(existingVersion))
          : null;
        const shouldUseRow = !metaUpdate[ct]
          || (versionComparison !== null && versionComparison > 0)
          || (versionComparison === 0 && normalizedRowStatus === 'accepted' && existingStatus !== 'accepted');

        if (shouldUseRow) {
          const msg = r.consent_message || r.message || r.consent || '';
          if (msg) contentUpdate[ct] = msg;
          const color = r.color || r.colour || metadata.color || metadata.colour || null;
          const iconName = metadata.iconName || metadata.icon || r.iconName || r.icon || 'Shield';
          const usage = (r.notified_percent != null) ? Number(r.notified_percent) : (r.notified || 0);
          // const ver = r.consent_version || r.version || metadata.version || null;
          const ver = serverVersion;
          metaUpdate[ct] = {
            id: r.id,
            label: metadata.label || raw || ct,
            color: color || '#2563eb',
            iconName: iconName,
            usage: Number(usage || 0),
            version: ver || null,
            status: normalizedRowStatus,
            language_code: r.language_code || r.language || 'en',
            language_name: r.language_name || 'English',
          };
        }
      }
      setRemoteTypes(types);
      setRemoteTypesLoaded(true);
      setContent({ ...initialContent, ...contentUpdate });
      setCustomTypeMeta({ ...metaUpdate });
      setVersionHistory(prev => {
        const next = { ...prev };
        Object.entries(serverHistory).forEach(([typeKey, serverEntries]) => {
          const localEntries = Array.isArray(next[typeKey]) ? next[typeKey] : [];
          const uniqueEntries = serverEntries.filter((entry, index, entries) => {
            const versionKey = String(entry.version).trim().replace(/^v/i, '').toLowerCase();
            return entries.findIndex(candidate => (
              String(candidate.version).trim().replace(/^v/i, '').toLowerCase() === versionKey
            )) === index;
          });
          next[typeKey] = uniqueEntries.map(serverEntry => {
            const localEntry = localEntries.find(entry => String(entry.version) === serverEntry.version) || {};
            return { ...localEntry, ...serverEntry };
          });
        });
        saveVersionHistory(next);
        return next;
      });
      try {
        const tsec = res.data && (res.data.tenant_sector || res.data.tenantSector || null);
        if (tsec) setTenantSector(String(tsec).trim().toLowerCase());
      } catch (e) { }
    } catch (e) {
      console.warn('Failed to refresh consent types', e);
    }
  }, [tenantId]);

  const handleBackClick = () => {
    if (props && typeof props.onBack === 'function') {
      props.onBack();
    } else {
      window.history.back();
    }
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    setNewConsentType('');
    setNewConsentContent('');
    setNewConsentVersion('');
    setShowOnlyCustom(false);
  };

  const handleSubmitNewConsent = async () => {
    if (!newConsentType) {
      Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please select a Consent Type', customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    if ((showOnlyCustom || newConsentType === 'custom') && !newConsentCustomLabel.trim()) {
      Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please enter a Custom Consent Type Label', customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    if (!newConsentIconName) {
      Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please select an Icon Shape & Design', customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    if (!newConsentColor) {
      Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please select an Icon Color', customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    if (!newConsentVersion.trim()) {
      Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please enter a Consent Version', customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    if (!stripHtml(newConsentContent).trim()) {
      Swal.fire({ icon: 'warning', title: 'Required Field Missing', text: 'Please enter Consent Content', customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    // Keep a stable ASCII key in storage. The selected language belongs in
    // metadata; storing a localized label as the type makes non-Latin types
    // normalize to an empty key and disappear from later dropdowns.
    let consentTypeToSend = newConsentType;
    if (newConsentType === 'custom') {
      consentTypeToSend = newConsentCustomLabel.trim();
    }
    try {
      if (!tenantId) {
        Swal.fire({ icon: 'warning', title: 'Missing Tenant', text: 'Cannot create consent: tenant_id not set.', customClass: { popup: 'swal-popup-custom' } });
        return;
      }
      const consentKey = normalizeKey(consentTypeToSend);
      const trimmedVersion = newConsentVersion.trim();
      const builtInType = CONSENT_TYPES.find(type => type.key === newConsentType);
      const consentTypeLabel = newConsentType === 'custom'
        ? newConsentCustomLabel.trim()
        : (LOCALIZED_MAIN_CONSENT_TYPES[builtInType?.key]?.[newConsentLanguage] || builtInType?.label || consentTypeToSend);
      const metadataObj = {
        iconName: newConsentIconName,
        color: newConsentColor,
        label: consentTypeLabel,
        language_code: newConsentLanguage,
        language_name: CONSENT_LANGUAGE_OPTIONS.find(item => item.value === newConsentLanguage)?.label || newConsentLanguage,
      };
      const payload = {
        tenant_id: tenantId,
        consent_type: consentKey,
        consent_message: sanitize(newConsentContent),
        consent_metadata: JSON.stringify(metadataObj),
        language_code: newConsentLanguage,
        language_name: metadataObj.language_name,
        color: newConsentColor,
        notified_percent: Number(newConsentNotified),
        ...(trimmedVersion ? { consent_version: trimmedVersion } : {}),
      };
      const res = await axios.post(`${API_BASE}/manager/new-consent`, payload, { headers: { 'X-Tenant-ID': tenantId } });
      const notified = res.data?.notified || [];

      // ── FIX: update content + customTypeMeta including version immediately ──
      setContent(prev => ({ ...prev, [consentKey]: sanitize(newConsentContent) }));
      setCustomTypeMeta(prev => ({
        ...prev,
        [consentKey]: {
          label: consentTypeToSend,
          color: newConsentColor,
          iconName: newConsentIconName,
          usage: Number(newConsentNotified),
          version: trimmedVersion || null,
          // language_code: 'en',
          // language_name: 'English',
          language_code: newConsentLanguage,
          language_name: metadataObj.language_name,
        }
      }));
      setRemoteTypes(prev => {
        if (prev && prev.includes(consentKey)) return prev;
        return [consentKey].concat(prev || []);
      });

      // If a version was entered, also push it into the version history
      if (trimmedVersion) {
        const setAt = new Date().toISOString();
        setVersionHistory(prev => {
          const next = { ...prev };
          const existing = Array.isArray(next[consentKey]) ? [...next[consentKey]] : [];
          if (!existing.find(e => e.version === trimmedVersion && !e.rejectedAt)) {
            existing.unshift({ version: trimmedVersion, setAt, rejectedAt: null });
          }
          next[consentKey] = existing;
          saveVersionHistory(next);
          return next;
        });
      }

      Swal.fire({ icon: 'success', title: 'Consent Created', html: `<div style="text-align:left">Consent created successfully.<br><br><strong>Notified:</strong> ${notified.join(', ') || 'none'}</div>`, customClass: { popup: 'swal-popup-custom' } });
      setSelectedType(consentKey);
      handleCloseCreateForm();
      refreshConsentData(tenantId);
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: 'error', title: 'Create Failed', text: 'Failed to create consent. See console for details.', customClass: { popup: 'swal-popup-custom' } });
    }
  };

  useEffect(() => {
    let active = true;
    const applyTenant = async (nextTenant) => {
      try {
        if (nextTenant) setTenantId(Number(nextTenant));
        else setTenantId(null);
        await refreshConsentData(nextTenant || null);
      } catch (e) {
        console.warn('Failed to fetch consent types', e);
      }
    };

    let initialTenant = Number(localStorage.getItem('tenant_id')) || null;
    try {
      if ((!initialTenant || initialTenant === 0) && typeof window !== 'undefined') {
        if (window.USER && window.USER.tenant_id) initialTenant = Number(window.USER.tenant_id);
        else if (window.__USER__ && window.__USER__.tenant_id) initialTenant = Number(window.__USER__.tenant_id);
      }
    } catch (e) { }

    const tenantHandler = (ev) => {
      try {
        const v = (ev && ev.detail && ev.detail.tenant_id) || Number(localStorage.getItem('tenant_id')) || null;
        if (v) {
          applyTenant(Number(v));
        }
      } catch (e) { }
    };

    window.addEventListener('tenantChanged', tenantHandler);
    applyTenant(initialTenant || null);

    return () => {
      active = false;
      window.removeEventListener('tenantChanged', tenantHandler);
    };
  }, [refreshConsentData]);

  // const displayTypes = (remoteTypes && remoteTypes.length > 0 ? remoteTypes : CONSENT_TYPES.map(t => t.key)).map(tk => {
  // Determine default keys: if tenantSector is provided, try to filter defaults to sector-relevant templates
  const defaultKeys = useMemo(() => {
    if (tenantSector) {
      const sectorTemplates = getTemplatesBySector(tenantSector);
      if (sectorTemplates && sectorTemplates.length > 0) {
        return sectorTemplates.map(t => t.id);
      }
      const norm = normalizeKey(tenantSector);
      const matched = CONSENT_TYPES.filter(t => normalizeKey(t.key) === norm || normalizeKey(t.label).includes(norm)).map(t => t.key);
      if (matched && matched.length > 0) return matched;
    }
    return CONSENT_TYPES.map(t => t.key);
  }, [tenantSector]);

  const displayTypes = useMemo(() => {
    // const source = (remoteType && remoteType.length > 0) ? remoteType : defaultKeys);
    const source = remoteTypesLoaded ? remoteTypes : defaultKeys;
    return source.map(tk => {
      // First check if it's a mockConsentData template (these will have template object info)
      let templateObj = null;
      // if (!remoteTypes || remoteTypes.length === 0) {
      if (!remoteTypesLoaded) {
        // When showing defaults from mockConsentData
        const sectorTemplates = getTemplatesBySector(tenantSector);
        templateObj = sectorTemplates.find(t => t.id === tk);
      }

      if (templateObj) {
        return {
          key: tk,
          label: templateObj.name,
          description: templateObj.description,
          icon: Shield,
          color: '#2563eb',
          usage: templateObj.mandatory ? 100 : 50,
          isMockTemplate: true
        };
      }

      const found = CONSENT_TYPES.find(c => normalizeKey(c.key) === normalizeKey(tk) || normalizeKey(c.label) === normalizeKey(tk));
      const key = String(tk);
      const cardKey = normalizeKey(tk);
      const meta = (customTypeMeta && (customTypeMeta[cardKey] || customTypeMeta[key])) || {};

      const iconName = meta.iconName || (found ? (ICON_OPTIONS.find(i => i.component === found.icon)?.name || 'Shield') : 'Shield');
      const iconComp = ICON_OPTIONS.find(i => i.name === iconName)?.component || (found ? found.icon : Shield);
      const color = meta.color || (found ? found.color : '#2563eb');
      const label = meta.label || (found ? found.label : formatDisplayLabel(key));
      const description = found ? found.description : '';
      const usage = meta.usage !== undefined ? meta.usage : (found ? found.usage : 0);

      return { key: tk, label, description, icon: iconComp, color, usage, iconName };
    });
    // }, [remoteTypes, defaultKeys, customTypeMeta, tenantSector]);
  }, [remoteTypes, remoteTypesLoaded, defaultKeys, customTypeMeta, tenantSector]);

  // ─── Version Management Handlers ──────────────────────────────────────────
  const showCardTooltip = (e, text) => {
    if (!e?.currentTarget || !text) {
      setCardTooltip({ visible: false, text: '', x: 0, y: 0 });
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setCardTooltip({
      visible: true,
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const hideCardTooltip = () => {
    setCardTooltip({ visible: false, text: '', x: 0, y: 0 });
  };

  const handleOpenVersionModal = (e, key) => {
    // e.stopPropagation();
    e?.preventDefault();
    e?.stopPropagation();
    setShowVersionModal(key);
    setVersionModalStep('view');
    setPendingNewVersion('');
  };

  const handleCloseVersionModal = () => {
    setShowVersionModal(null);
    setVersionModalStep('view');
    setPendingNewVersion('');
    setVersionRejecting(false);
    setSelectedVersionEntry(null);
  };

  // const handleRejectOldVersion = async () => {
  const handleRejectOldVersion = async (event) => {
    event?.stopPropagation();

    if (versionRejecting) return;

    const key = normalizeKey(showVersionModal);
    const currentVer = (customTypeMeta && customTypeMeta[key]) ? customTypeMeta[key].version : null;

    // const confirmResult = await Swal.fire({
    //   title: 'Reject Current Version?',
    //   html: `This will mark the current version <strong>${currentVer || '(none)'}</strong> as <em>rejected</em> for <strong>${key}</strong>. <br> <br>you must then enter a new version to replace it.`,
    //   icon: 'warning',
    //   showCancelButton: true,
    //   confirmButtonText: 'Reject',
    //   cancelButtonText: 'Cancel',
    //   buttonsStyling: true,
    //   confirmButtonColor: '#dc2626',
    //   cancelButtonColor: '#6b7280',
    // });
    // if (!confirmResult.isConfirmed) return;

    if (!key) {
      Swal.fire({ icon: 'warning', title: 'No consent selected', text: 'Please reopen the version modal and try again.', customClass: { popup: 'swal-popup-custom' } });
      return;
    }

    setVersionRejecting(true);
    try {
      if (tenantId && currentVer) {
        try {
          await axios.post(`${API_BASE}/manager/reject-consent-version`, {
            tenant_id: Number(localStorage.getItem('tenant_id')) || tenantId,
            consent_type: key,
            consent_version: currentVer,
            // }, {headers: { 'X-Tenant-ID': tenantId}});
          }, {
            headers: {
              'X-Tenant-ID': tenantId,
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
              'X-Skip-Redirect-On-401': '1',
            },
            skipRedirectOn401: true,
          });
        } catch (apiErr) {
          console.warn('reject-consent-version API not available or failed — continuing locally', apiErr);
        }
      }

      const rejectedAt = new Date().toISOString();
      setVersionHistory(prev => {
        const next = { ...prev };
        const existing = Array.isArray(next[key]) ? [...next[key]] : [];
        const idx = existing.findIndex(e => e.version === currentVer && !e.rejectedAt);
        if (idx !== -1) {
          existing[idx] = { ...existing[idx], rejectedAt };
        } else {
          existing.unshift({ version: currentVer || '(none)', setAt: null, rejectedAt });
        }
        next[key] = existing;
        saveVersionHistory(next);
        return next;
      });

      setPendingNewVersion('');
      setVersionModalStep('update');
    } catch (e) {
      console.error('handleRejectOldVersion error', e);
      Swal.fire({ icon: 'error', title: 'Reject Failed', text: 'The version could not be rejected. Please try again.', customClass: { popup: 'swal-popup-custom' } });
    } finally {
      setVersionRejecting(false);
    }
  };

  const handleApplyNewVersion = async () => {
    const key = normalizeKey(showVersionModal);
    const newVer = pendingNewVersion.trim();
    if (!newVer) {
      Swal.fire({ icon: 'warning', title: 'Version Required', text: 'Please enter a version string (e.g. 2.0, 1.1-beta).', customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    const currentVer = versionModalCurrentVer ? String(versionModalCurrentVer).trim() : '';
    const historicalVersions = [currentVer, ...versionModalHistory.map(entry => entry.version)]
      .filter(Boolean)
      .reduce((versions, version) => {
        if (!versions.some(existing => String(existing) === String(version))) versions.push(String(version));
        return versions;
      }, []);
    const highestVersion = historicalVersions.reduce((highest, version) => {
      if (!highest) return version;
      const comparison = compareConsentVersions(version, highest);
      return comparison !== null && comparison > 0 ? version : highest;
    }, '');
    const versionPlan = getConsentVersionUpdatePlan(highestVersion, newVer);
    if (versionPlan.invalid) {
      Swal.fire({ icon: 'error', title: 'Invalid Version', text: `Version ${newVer} must be greater than the latest recorded version ${highestVersion}.`, customClass: { popup: 'swal-popup-custom' } });
      return;
    }
    try {
      if (tenantId) {
        try {
          const versionResponse = await axios.post(`${API_BASE}/manager/new-consent`, {
            tenant_id: Number(localStorage.getItem('tenant_id')) || tenantId,
            consent_type: key,
            consent_message: content[key] || '',
            consent_metadata: '',
            consent_version: newVer,
            version_update: true,
            status: 'under_process',
          }, { headers: { 'X-Tenant-ID': tenantId } });
          if (!versionResponse?.data?.success) {
            throw new Error(versionResponse?.data?.detail || 'The new version was rejected by the server.');
          }
          await refreshConsentData(tenantId);
        } catch (apiErr) {
          const detail = apiErr?.response?.data?.detail || 'The new version was rejected by the server.';
          Swal.fire({ icon: 'error', title: 'Version Update Failed', text: detail, customClass: { popup: 'swal-popup-custom' } });
          return;
        }
      }
      const setAt = new Date().toISOString();
      // ── FIX: update customTypeMeta so card badge re-renders immediately ──
      setCustomTypeMeta(prev => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          version: newVer,
          status: 'under_process',
        },
      }));
      setVersionHistory(prev => {
        const next = { ...prev };
        const existing = Array.isArray(next[key]) ? [...next[key]] : [];
        existing.unshift({ version: newVer, setAt, rejectedAt: null });
        next[key] = existing;
        saveVersionHistory(next);
        return next;
      });
      Swal.fire({ icon: 'success', title: 'Version Updated', text: `Version updated to ${newVer}`, customClass: { popup: 'swal-popup-custom' }, zIndex: 4000 });
      handleCloseVersionModal();
    } catch (e) {
      console.error('handleApplyNewVersion error', e);
    }
  };

  const slidingFormStyles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
      opacity: showCreateForm ? 1 : 0,
      visibility: showCreateForm ? 'visible' : 'hidden',
      transition: 'opacity 0.3s ease, visibility 0.3s ease',
    },
    form: {
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: '500px', maxWidth: '90vw', background: '#fff',
      boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)', zIndex: 1001,
      transform: showCreateForm ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease', display: 'flex', flexDirection: 'column',
    },
    formHeader: {
      padding: '24px', borderBottom: '1px solid #e2e8f0',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#fff',
    },
    formTitle: { fontSize: '20px', fontWeight: '600', margin: 0 },
    closeBtn: {
      background: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px',
      width: '36px', height: '36px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', cursor: 'pointer', color: '#fff', transition: 'all 0.2s ease',
    },
    formBody: { flex: 1, padding: '24px', overflowY: 'auto' },
    formGroup: { marginBottom: '24px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#1e293b' },
    select: {
      width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px',
      fontSize: '14px', color: '#1e293b', background: '#fff', transition: 'all 0.2s ease', outline: 'none',
    },
    input: {
      width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px',
      fontSize: '14px', color: '#1e293b', background: '#fff', outline: 'none',
      boxSizing: 'border-box', transition: 'border-color 0.2s ease',
    },
    editorWrapper: { marginTop: '8px' },
    formFooter: { padding: '24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px' },
    submitBtn: {
      flex: 1, padding: '12px 24px',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: '#fff', border: 'none', borderRadius: '8px',
      fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease',
    },
    cancelFormBtn: {
      flex: 1, padding: '12px 24px', background: '#fff', color: '#64748b',
      border: '2px solid #e2e8f0', borderRadius: '8px',
      fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease',
    },
  };

  const versionModalKey = showVersionModal ? normalizeKey(showVersionModal) : null;
  const versionModalMeta = versionModalKey && customTypeMeta ? customTypeMeta[versionModalKey] : null;
  const versionModalCurrentVer = versionModalMeta ? versionModalMeta.version : null;
  const versionModalHistory = (versionModalKey && versionHistory[versionModalKey]) || [];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>Create Consent</h2>
        </div>
        <div style={styles.headerRight}>
          <button
            className="tour-target-create-consent-btn"
            style={styles.createBtn}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(37, 99, 235, 0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(37, 99, 235, 0.3)'; }}
            onClick={handleCreateConsent}
          >
            Create Consent
          </button>
          <button
            style={styles.backBtn}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
            onClick={() => setGuideOpen(true)}
          >
            📖 Page Guide
          </button>
          <button
            style={styles.backBtn}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2563eb'; }}
            onClick={handleBackClick}
          >
            <ArrowLeft size={18} />
            Back
          </button>
        </div>
      </div>

      {cardTooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: cardTooltip.x,
            top: cardTooltip.y,
            transform: 'translate(-50%, -100%)',
            background: '#111827',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            boxShadow: '0 10px 25px rgba(15, 23, 42, 0.25)',
            zIndex: 1600,
            pointerEvents: 'none',
          }}
        >
          {cardTooltip.text}
        </div>
      )}

      {/* Consent Cards */}
      <div style={styles.gridContainer}>
        <div style={styles.grid}>
          {displayTypes.map(type => {
            const IconComponent = type.icon;
            const isSelected = selectedType === type.key;
            const isHovered = hoveredCard === type.key;
            const cardActionsVisible = isHovered || tourActive;
            const cardKey = normalizeKey(type.key);
            const cardMeta = customTypeMeta && customTypeMeta[cardKey];
            const cardVersion = cardMeta ? cardMeta.version : null;
            const cardStatus = String(cardMeta?.status || '').toLowerCase();
            const isAccepted = cardStatus === 'accepted';
            const cardDisplayLabel = type.label || (type.key ? formatDisplayLabel(type.key) : '');
            return (
              <div
                key={type.key}
                className="consent-card tour-target-consent-card"
                style={{
                  ...styles.card,
                  ...(isSelected ? styles.cardSelected : {}),
                  height: '320px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  transform: isHovered || isSelected ? 'translateY(-6px)' : 'translateY(0)',
                  boxShadow: isSelected
                    ? '0 12px 40px rgba(37, 99, 235, 0.3)'
                    : isHovered
                      ? '0 8px 32px rgba(0, 0, 0, 0.12)'
                      : '0 4px 20px rgba(0, 0, 0, 0.08)',
                }}
                onClick={() => setSelectedType(type.key)}
                onMouseEnter={() => setHoveredCard(type.key)}
                onMouseLeave={() => { setHoveredCard(null); hideCardTooltip(); }}
              >
                {/* Version Badge (top-left) */}
                <button
                  type="button"
                  className="tour-target-version-badge"
                  aria-label={cardVersion ? `Version ${cardVersion} — click to manage` : 'No version set — click to set one'}
                  onClick={(e) => {
                    handleOpenVersionModal(e, type.key);
                    showCardTooltip(e, cardVersion ? `Version ${cardVersion} — click to manage` : 'No version set — click to set one');
                  }}
                  onFocus={(e) => showCardTooltip(e, cardVersion ? `Version ${cardVersion} — click to manage` : 'No version set — click to set one')}
                  onBlur={hideCardTooltip}
                  style={{
                    position: 'absolute', top: 10, left: 10,
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                    padding: cardVersion ? '4px 10px 4px 7px' : '4px 9px',
                    borderRadius: '999px',
                    border: cardVersion ? '1.5px solid #bfdbfe' : '1.5px dashed #cbd5e1',
                    background: cardVersion
                      ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                      : '#f8fafc',
                    cursor: 'pointer', fontSize: '11px', fontWeight: '700',
                    color: cardVersion ? '#1d4ed8' : '#94a3b8',
                    letterSpacing: '0.03em', transition: 'all 0.2s ease', zIndex: 10,
                    boxShadow: cardVersion ? '0 1px 4px rgba(37,99,235,0.13)' : 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = cardVersion
                      ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : '#2563eb';
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.borderColor = '#2563eb';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(37,99,235,0.35)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                    showCardTooltip(e, cardVersion ? `Version ${cardVersion} — click to manage` : 'No version set — click to set one');
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = cardVersion
                      ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : '#f8fafc';
                    e.currentTarget.style.color = cardVersion ? '#1d4ed8' : '#94a3b8';
                    e.currentTarget.style.borderColor = cardVersion ? '#bfdbfe' : '#cbd5e1';
                    e.currentTarget.style.boxShadow = cardVersion ? '0 1px 4px rgba(37,99,235,0.13)' : 'none';
                    e.currentTarget.style.transform = 'scale(1)';
                    hideCardTooltip();
                  }}
                >
                  <Tag size={10} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                  {cardVersion ? `v${cardVersion}` : 'Set ver'}
                </button>

                {/* Action Buttons */}
                <div
                  style={{
                    ...styles.cardActions,
                    top: '0.7rem',
                    right: '0.7rem',
                    gap: '0.35rem',
                    opacity: cardActionsVisible ? 1 : 0,
                    visibility: cardActionsVisible ? 'visible' : 'hidden',
                    padding: '0.2rem',
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(6px)',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <button
                    type="button"
                    style={styles.actionBtn}
                    aria-label="View consent"
                    onClick={(e) => { e.stopPropagation(); handleCardClick(type.key, 'view'); }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; showCardTooltip(e, 'View consent'); }}
                    onMouseMove={(e) => showCardTooltip(e, 'View consent')}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; hideCardTooltip(); }}
                    onFocus={(e) => showCardTooltip(e, 'View consent')}
                    onBlur={hideCardTooltip}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    type="button"
                    className="tour-target-edit-action"
                    style={{
                      ...styles.actionBtn,
                      color: isAccepted ? '#94a3b8' : '#64748b',
                    }}
                    aria-label={isAccepted ? "Edit disabled (Accepted)" : "Edit consent"}
                    onClick={(e) => { e.stopPropagation(); handleCardClick(type.key, 'edit'); }}
                    onMouseEnter={(e) => {
                      if (!isAccepted) {
                        e.currentTarget.style.background = '#10b981';
                        e.currentTarget.style.color = '#fff';
                      }
                      showCardTooltip(e, isAccepted ? 'Locked 🔒 (Create V2 to edit)' : 'Edit consent');
                    }}
                    onMouseMove={(e) => showCardTooltip(e, isAccepted ? 'Locked 🔒 (Create V2 to edit)' : 'Edit consent')}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = isAccepted ? '#94a3b8' : '#64748b'; hideCardTooltip(); }}
                    onFocus={(e) => showCardTooltip(e, isAccepted ? 'Locked 🔒 (Create V2 to edit)' : 'Edit consent')}
                    onBlur={hideCardTooltip}
                  >
                    {isAccepted ? <Lock size={16} /> : <Edit size={16} />}
                  </button>
                  {!isAccepted && (
                    <button
                      type="button"
                      style={styles.actionBtn}
                      aria-label="Accept consent"
                      onClick={(e) => { e.stopPropagation(); handleAcceptConsent(type.key); }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.color = '#fff'; showCardTooltip(e, 'Accept & Lock consent'); }}
                      onMouseMove={(e) => showCardTooltip(e, 'Accept & Lock consent')}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; hideCardTooltip(); }}
                      onFocus={(e) => showCardTooltip(e, 'Accept & Lock consent')}
                      onBlur={hideCardTooltip}
                    >
                      <CheckCircle size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="tour-target-subcategories"
                    disabled={!isAccepted}
                    style={{
                      ...styles.actionBtn,
                      position: 'relative',
                      color: isAccepted ? '#64748b' : '#cbd5e1',
                      opacity: isAccepted ? 1 : 0.55,
                      cursor: isAccepted ? 'pointer' : 'not-allowed',
                    }}
                    aria-label={isAccepted ? 'Sub-categories' : 'Sub-categories disabled until accepted'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAccepted) return;
                      if (props && typeof props.onOpenSubcategories === 'function') {
                        try {
                          props.onOpenSubcategories({
                            key: type.key,
                            label: type.label || type.key,
                            id: customTypeMeta?.[normalizeKey(type.key)]?.id || null,
                          });
                        } catch (ex) { }
                      } else {
                        window.open(`${window.location.origin}/#subcategories/${type.key}`, '_blank');
                      }
                    }}
                    onMouseEnter={(e) => { if (isAccepted) { e.currentTarget.style.background = '#7c3aed'; e.currentTarget.style.color = '#fff'; } showCardTooltip(e, isAccepted ? 'Sub-categories' : 'Accept & Lock consent first'); }}
                    onMouseMove={(e) => showCardTooltip(e, isAccepted ? 'Sub-categories' : 'Accept & Lock consent first')}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = isAccepted ? '#64748b' : '#cbd5e1'; hideCardTooltip(); }}
                    onFocus={(e) => showCardTooltip(e, isAccepted ? 'Sub-categories' : 'Accept & Lock consent first')}
                    onBlur={hideCardTooltip}
                  >
                    <FileText size={16} />
                  </button>
                  <button
                    type="button"
                    disabled={!isAccepted}
                    style={{
                      ...styles.actionBtn,
                      position: 'relative',
                      color: isAccepted ? '#64748b' : '#cbd5e1',
                      opacity: isAccepted ? 1 : 0.55,
                      cursor: isAccepted ? 'pointer' : 'not-allowed',
                    }}
                    aria-label={isAccepted ? 'Multilingual consent' : 'Multilingual consent disabled until accepted'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAccepted) return;
                      const cardMeta = customTypeMeta?.[normalizeKey(type.key)] || customTypeMeta?.[type.key] || {};
                      if (props && typeof props.onOpenMultilingualConsent === 'function') {
                        try {
                          props.onOpenMultilingualConsent({
                            key: type.key,
                            id: cardMeta.id,
                            label: type.label,
                            version: cardMeta.version || cardMeta.consent_version || '1.0.0',
                            languageCode: cardMeta.language_code || cardMeta.language || 'en',
                            languageName: cardMeta.language_name || cardMeta.language || 'English',
                          });
                        } catch (ex) { }
                      } else {
                        window.open(`${window.location.origin}/#multilingual-consent/${type.key}`, '_blank');
                      }
                    }}
                    onMouseEnter={(e) => { if (isAccepted) { e.currentTarget.style.background = '#7c3aed'; e.currentTarget.style.color = '#fff'; } showCardTooltip(e, isAccepted ? 'Multilingual consent' : 'Accept & Lock consent first'); }}
                    onMouseMove={(e) => showCardTooltip(e, isAccepted ? 'Multilingual consent' : 'Accept & Lock consent first')}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = isAccepted ? '#64748b' : '#cbd5e1'; hideCardTooltip(); }}
                    onFocus={(e) => showCardTooltip(e, isAccepted ? 'Multilingual consent' : 'Accept & Lock consent first')}
                    onBlur={hideCardTooltip}
                  >
                    <Languages size={16} />
                  </button>
                </div>

                <div style={{ ...styles.cardIcon, color: type.color, transform: isHovered ? 'scale(1.1)' : 'scale(1)', flexShrink: 0, marginTop: '1.8rem', marginBottom: '0.4rem' }}>
                  <IconComponent size={44} strokeWidth={1.5} />
                </div>

                {/* ── FIX: title is clamped to 2 lines with ellipsis so a long
                    consent name stays inside the card; full name is available
                    on hover via the native title tooltip ── */}
                <div
                  title={cardDisplayLabel}
                  style={{
                    ...styles.cardTitle,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                    lineHeight: '1.3',
                    flexShrink: 0,
                  }}
                >
                  {cardDisplayLabel}
                </div>

                {/* Status Badge (Centered below Card Title) */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '6px', marginBottom: '8px', flexShrink: 0 }}>
                  <span
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '3px 10px', borderRadius: '999px',
                      border: isAccepted ? '1px solid #86efac' : '1px solid #fcd34d',
                      background: isAccepted ? '#dcfce7' : '#fef3c7',
                      color: isAccepted ? '#15803d' : '#b45309',
                      fontSize: '11px', fontWeight: '700', letterSpacing: '0.02em',
                      boxShadow: isAccepted ? '0 1px 4px rgba(22, 163, 74, 0.12)' : '0 1px 4px rgba(217, 119, 6, 0.12)',
                    }}
                    title={isAccepted ? 'Accepted & Locked 🔒' : 'Under Process (Edit Enabled ✏️)'}
                  >
                    {isAccepted ? '🔒 ACCEPTED' : '✏️ UNDER PROCESS'}
                  </span>
                </div>

                {/* ── FIX: description now sits in a flexible, clipped region
                    with a small "read more" icon so overflow never pushes
                    past the card edge — clicking it opens the full view modal ── */}
                <div style={{ flex: 1, minHeight: 0 }}>
                  <div
                    style={{
                      ...styles.cardDesc,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitize(content[normalizeKey(type.key)] || content[type.key] || type.description || '') }}
                  />
                </div>
                {/* ── Version pill shown inside card body when version is set ── */}
                {/* {cardVersion && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    marginTop: '8px', padding: '3px 10px', borderRadius: '999px',
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    fontSize: '12px', fontWeight: '600', color: '#1d4ed8',
                    letterSpacing: '0.02em', flexShrink: 0, alignSelf: 'flex-start',
                  }}>
                    <Tag size={10} />
                    v{cardVersion}
                  </div>
                )} */}
                {/* NOTE: .env version link removed as requested */}
              </div>
            );
          })}
        </div>
        {error && <div style={styles.error}>{error}</div>}
      </div>

      {/* Modal */}
      {modalOpen && modalType && (
        <div style={styles.modalOverlay} onClick={handleModalClose}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderLeft}>
                {React.createElement(ICON_OPTIONS.find(i => i.name === (modalMode === 'edit' ? modalEditIconName : (modalType.iconName || 'Shield')))?.component || modalType.icon || Shield, {
                  size: 32,
                  color: modalMode === 'edit' ? modalEditColor : (modalType.color || '#2563eb'),
                  strokeWidth: 1.5
                })}
                <span style={styles.modalTitle}>{modalEditType || modalType.label || (modalType.key ? formatDisplayLabel(modalType.key) : '')}</span>
                {(() => {
                  const mKey = normalizeKey(modalType.key);
                  const mMeta = customTypeMeta?.[mKey] || customTypeMeta?.[modalType.key];
                  const mStatus = String(mMeta?.status || '').toLowerCase();
                  const isAcc = mStatus === 'accepted';
                  return (
                    <span style={{
                      background: isAcc ? '#dcfce7' : '#fef3c7',
                      color: isAcc ? '#15803d' : '#b45309',
                      border: isAcc ? '1px solid #86efac' : '1px solid #fcd34d',
                      borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: '700',
                      marginLeft: '8px'
                    }}>
                      {isAcc ? '🔒 ACCEPTED' : '✏️ UNDER PROCESS'}
                    </span>
                  );
                })()}
              </div>
              <button
                style={styles.modalClose}
                onClick={handleModalClose}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
              >×</button>
            </div>
            <div style={styles.modalContent}>
              {modalMode === 'edit' ? (
                <div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                      Consent Title <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={modalEditType}
                      onChange={(e) => setModalEditType(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '2px solid #e2e8f0' }}
                    />
                    <div style={{ marginTop: '6px', color: '#64748b', fontSize: '0.8rem' }}>Changing this will rename the stored type.</div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '14px', margin: '0.75rem 0' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                        Icon Shape & Design <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <select
                        required
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#fff', fontSize: '0.9rem' }}
                        value={modalEditIconName}
                        onChange={(e) => setModalEditIconName(e.target.value)}
                      >
                        {ICON_OPTIONS.map(opt => (
                          <option key={opt.name} value={opt.name}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                        Icon Color <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="color"
                          required
                          value={modalEditColor}
                          onChange={(e) => setModalEditColor(e.target.value)}
                          style={{ width: '48px', height: '38px', padding: '3px', borderRadius: '8px', border: '2px solid #e2e8f0', cursor: 'pointer', background: '#fff' }}
                        />
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '8px',
                          background: `${modalEditColor}18`, border: `1px solid ${modalEditColor}40`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: modalEditColor
                        }}>
                          {React.createElement(ICON_OPTIONS.find(i => i.name === modalEditIconName)?.component || Shield, { size: 20 })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                      Version <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      readOnly
                      disabled
                      value={newConsentVersionForEdit}
                      aria-label="Current consent version"
                      title="Version is locked here. Use Set Version to create a new version."
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' }}
                    />
                    <div style={{ marginTop: '6px', color: '#64748b', fontSize: '0.8rem' }}>
                      Version is locked during normal editing. Use Set Version to create a higher version.
                    </div>
                  </div>

                  <div style={{ marginBottom: '6px' }}>
                    <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                      Consent Content <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                  </div>
                  <ReactQuill theme="snow" value={editorValue} onChange={setEditorValue} modules={quillModules} formats={quillFormats} />
                  <div style={{ marginTop: '0.5rem', color: '#64748b', fontSize: '0.85rem' }}>
                    Formatting: <strong>B</strong>, <em>I</em>, <u>U</u>, color
                  </div>
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: sanitize(content[normalizeKey(modalType.key)] || content[modalType.key] || '') }} />
              )}
            </div>
            {modalMode === 'edit' ? (
              <div style={styles.modalActions}>
                {/* <button style={styles.saveBtn} onClick={handleSave} */}
                <button className="tour-target-modal-save" style={styles.saveBtn} onClick={handleSave}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#10b981'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >Save</button>
                <button
                  className="tour-target-modal-delete"
                  style={{ ...styles.cancelBtn, background: '#fff', color: '#64748b', border: '2px solid #fecaca' }}
                  onClick={handleDeleteConsent}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >Delete</button>
                <button style={styles.cancelBtn} onClick={handleModalClose}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >Cancel</button>
              </div>
            ) : (
              <div style={styles.modalActions}>
                {(() => {
                  const mKey = normalizeKey(modalType.key);
                  const mMeta = customTypeMeta?.[mKey] || customTypeMeta?.[modalType.key];
                  const mStatus = String(mMeta?.status || '').toLowerCase();
                  const isAcc = mStatus === 'accepted';
                  if (isAcc) {
                    return (
                      <>
                        <button
                          style={{ ...styles.editBtn, background: '#2563eb' }}
                          onClick={(e) => {
                            handleModalClose();
                            handleOpenVersionModal(e, modalType.key);
                          }}
                        >
                          <Tag size={18} /> Create New Version (V2)
                        </button>
                        <span style={{ fontSize: '13px', color: '#15803d', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Lock size={15} /> ACCEPTED & LOCKED 🔒
                        </span>
                      </>
                    );
                  }
                  return (
                    <>
                      <button style={styles.editBtn} onClick={() => setModalMode('edit')}>
                        <Edit size={18} /> Edit (Under Process ✏️)
                      </button>
                      <button
                        style={{ ...styles.saveBtn, background: '#059669', minWidth: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        onClick={() => handleAcceptConsent(modalType.key)}
                      >
                        <CheckCircle size={18} /> Accept Consent
                      </button>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sliding Create Form */}
      <div style={slidingFormStyles.overlay} onClick={handleCloseCreateForm}>
        <div style={slidingFormStyles.form} onClick={e => e.stopPropagation()}>
          <div style={slidingFormStyles.formHeader}>
            <h3 style={slidingFormStyles.formTitle}>Create New Consent</h3>
            <button
              style={slidingFormStyles.closeBtn}
              onClick={handleCloseCreateForm}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
            ><X size={20} /></button>
          </div>
          <div style={slidingFormStyles.formBody}>
            {!showOnlyCustom && (
              <div style={slidingFormStyles.formGroup}>
                <label style={slidingFormStyles.label}>
                  Consent Type <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  required
                  style={slidingFormStyles.select}
                  value={newConsentType}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewConsentType(val);
                    if (val && val !== 'custom') setNewConsentContent(content[val] || '');
                    else if (val === 'custom') setNewConsentContent('');
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  <option value="">Select a consent type</option>
                  {CONSENT_TYPES.map(type => (
                    <option key={type.key} value={type.key}>{type.label}</option>
                  ))}
                  <option value="custom">Custom (enter label below)</option>
                </select>
              </div>
            )}
            <div style={slidingFormStyles.formGroup}>
              <label style={slidingFormStyles.label}>
                Consent Language <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                required
                style={slidingFormStyles.select}
                value={newConsentLanguage}
                onChange={(e) => setNewConsentLanguage(e.target.value)}
              >
                {CONSENT_LANGUAGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '14px' }}>
              <div style={slidingFormStyles.formGroup}>
                <label style={slidingFormStyles.label}>
                  Icon Shape & Design <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  required
                  style={slidingFormStyles.select}
                  value={newConsentIconName}
                  onChange={(e) => setNewConsentIconName(e.target.value)}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  {ICON_OPTIONS.map(opt => (
                    <option key={opt.name} value={opt.name}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={slidingFormStyles.formGroup}>
                <label style={slidingFormStyles.label}>
                  Icon Color <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="color"
                    required
                    value={newConsentColor}
                    onChange={(e) => setNewConsentColor(e.target.value)}
                    style={{ width: '48px', height: '38px', padding: '3px', borderRadius: '8px', border: '2px solid #e2e8f0', cursor: 'pointer', background: '#fff' }}
                  />
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '8px',
                    background: `${newConsentColor}18`, border: `1px solid ${newConsentColor}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: newConsentColor
                  }}>
                    {React.createElement(ICON_OPTIONS.find(i => i.name === newConsentIconName)?.component || Shield, { size: 20 })}
                  </div>
                </div>
              </div>
            </div>
            {(showOnlyCustom || newConsentType === 'custom') && (
              <div style={slidingFormStyles.formGroup}>
                <label style={slidingFormStyles.label}>
                  Custom Consent Type Label <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input type="text" required value={newConsentCustomLabel} onChange={(e) => setNewConsentCustomLabel(e.target.value)}
                  placeholder="e.g. 'Visitor Photo Consent'" style={slidingFormStyles.input}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }} />
              </div>
            )}
            <div style={slidingFormStyles.formGroup}>
              <label style={slidingFormStyles.label}>
                Consent Version <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input type="text" required value={newConsentVersion} onChange={(e) => setNewConsentVersion(e.target.value)}
                placeholder="e.g. 1.0, 2.1-beta" style={slidingFormStyles.input}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }} />
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>
                Version will appear on the consent card immediately after saving.
              </div>
            </div>
            <div style={slidingFormStyles.formGroup}>
              <label style={slidingFormStyles.label}>
                Consent Content <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={slidingFormStyles.editorWrapper}>
                <ReactQuill theme="snow" value={newConsentContent} onChange={setNewConsentContent}
                  modules={quillModules} formats={quillFormats} placeholder="Enter consent content here..." />
                <div style={{ marginTop: '8px', color: '#64748b', fontSize: '0.85rem' }}>
                  Formatting: <strong>B</strong>, <em>I</em>, <u>U</u>, color
                </div>
              </div>
            </div>
          </div>
          <div style={slidingFormStyles.formFooter}>
            <button style={slidingFormStyles.submitBtn} onClick={handleSubmitNewConsent}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >Submit Consent</button>
            <button style={slidingFormStyles.cancelFormBtn} onClick={handleCloseCreateForm}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >Cancel</button>
          </div>
        </div>
      </div>

      {/* VERSION MANAGEMENT MODAL */}
      {showVersionModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(4px)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={handleCloseVersionModal}
        >
          <style>{`
            @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
            @keyframes slideUp { from { transform:translateY(24px); opacity:0 } to { transform:translateY(0); opacity:1 } }
            .vm-history-row:hover { background: #f0f9ff !important; }
          `}</style>
          <div
            style={{
              background: '#fff', borderRadius: '20px', width: '520px',
              maxWidth: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 24px 80px rgba(0,0,0,0.22)', animation: 'slideUp 0.25s ease', overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '22px 24px 18px',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 60%, #3b82f6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '12px',
                  background: 'rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Tag size={20} color="#fff" strokeWidth={2} />
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: '700', fontSize: '17px', lineHeight: 1.2 }}>Version Management</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginTop: '2px', fontWeight: 500 }}>{versionModalKey}</div>
                </div>
              </div>
              <button onClick={handleCloseVersionModal}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '10px',
                  width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#fff', flexShrink: 0, transition: 'background 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              ><X size={18} /></button>
            </div>

            {/* Step indicator */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '14px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', gap: '8px' }}>
              {[
                { key: 'view', label: '1. Current', icon: <Eye size={13} /> },
                { key: 'reject', label: '2. Reject Old', icon: <AlertTriangle size={13} /> },
                { key: 'update', label: '3. Set New', icon: <CheckCircle size={13} /> },
              ].map((step, idx, arr) => {
                const active = versionModalStep === step.key;
                const done = (
                  (step.key === 'view' && (versionModalStep === 'reject' || versionModalStep === 'update')) ||
                  (step.key === 'reject' && versionModalStep === 'update')
                );
                return (
                  <React.Fragment key={step.key}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '5px 11px', borderRadius: '999px',
                      fontSize: '12px', fontWeight: '600',
                      background: active ? '#2563eb' : done ? '#d1fae5' : '#e2e8f0',
                      color: active ? '#fff' : done ? '#065f46' : '#94a3b8',
                      transition: 'all 0.25s', whiteSpace: 'nowrap',
                    }}>
                      {done ? <CheckCircle size={13} /> : step.icon}
                      {step.label}
                    </div>
                    {idx < arr.length - 1 && (
                      <div style={{ flex: 1, height: '2px', background: done ? '#6ee7b7' : '#e2e8f0', borderRadius: '1px', transition: 'background 0.3s' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
              {versionModalStep === 'view' && (
                <div>
                  <div style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    border: '1.5px solid #bfdbfe', borderRadius: '14px', padding: '20px',
                    marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px',
                  }}>
                    <div style={{
                      width: '52px', height: '52px', borderRadius: '14px',
                      background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                    }}>
                      <Tag size={24} color="#fff" strokeWidth={2} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Current Version</div>
                      <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e3a8a', lineHeight: 1 }}>
                        {versionModalCurrentVer ? `v${versionModalCurrentVer}` : <span style={{ color: '#94a3b8', fontSize: '18px', fontWeight: '600' }}>Not set</span>}
                      </div>
                    </div>
                    {versionModalCurrentVer && (
                      <div style={{
                        padding: '4px 12px', borderRadius: '999px', background: '#d1fae5', border: '1px solid #6ee7b7',
                        color: '#065f46', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px',
                      }}>
                        <CheckCircle size={11} />Active
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <button
                      type="button"
                      onClick={() => setHistoryOpen(p => ({ ...p, [versionModalKey]: !p[versionModalKey] }))}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
                        borderRadius: '10px', fontSize: '13px', fontWeight: '700', color: '#374151', transition: 'background 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <History size={15} color="#6366f1" />
                        Version History
                        {versionModalHistory.length > 0 && (
                          <span style={{ background: '#6366f1', color: '#fff', borderRadius: '999px', fontSize: '10px', fontWeight: '700', padding: '1px 7px' }}>{versionModalHistory.length}</span>
                        )}
                      </span>
                      {historyOpen[versionModalKey] ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
                    </button>
                    {historyOpen[versionModalKey] && (
                      <div style={{ marginTop: '8px' }}>
                        {versionModalHistory.length === 0 ? (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', background: '#f8fafc', borderRadius: '10px', border: '1.5px dashed #e2e8f0' }}>
                            No version history yet.
                          </div>
                        ) : (
                          <div style={{ border: '1.5px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '10px 14px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                              <span>Version</span><span>Set On</span><span>Rejected On</span>
                            </div>
                            {versionModalHistory.map((entry, idx) => (
                              <button type="button" key={idx} className="vm-history-row" onClick={() => setSelectedVersionEntry(entry)} style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '10px 14px', border: 'none', borderBottom: idx < versionModalHistory.length - 1 ? '1px solid #f1f5f9' : 'none', background: selectedVersionEntry?.version === entry.version ? '#f8fafc' : '#fff', fontSize: '12px', transition: 'background 0.15s', alignItems: 'center', textAlign: 'left', cursor: 'pointer' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <Tag size={11} color={entry.rejectedAt || ['inactive', 'rejected'].includes(String(entry.status).toLowerCase()) ? '#ef4444' : '#2563eb'} />
                                  <span style={{ fontWeight: '700', color: entry.rejectedAt || ['inactive', 'rejected'].includes(String(entry.status).toLowerCase()) ? '#ef4444' : '#1e3a8a', background: entry.rejectedAt || ['inactive', 'rejected'].includes(String(entry.status).toLowerCase()) ? '#fef2f2' : '#eff6ff', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', border: `1px solid ${entry.rejectedAt || ['inactive', 'rejected'].includes(String(entry.status).toLowerCase()) ? '#fecaca' : '#bfdbfe'}` }}>v{entry.version}</span>
                                </span>
                                <span style={{ color: '#374151', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {entry.setAt ? <><Clock size={10} color="#94a3b8" />{formatTs(entry.setAt)}</> : <span style={{ color: '#cbd5e1' }}>—</span>}
                                </span>
                                <span style={{ color: '#ef4444', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {entry.rejectedAt || ['inactive', 'rejected'].includes(String(entry.status).toLowerCase()) ? <><AlertTriangle size={10} color="#ef4444" />{entry.rejectedAt ? formatTs(entry.rejectedAt) : 'Inactive'}</> : <span style={{ color: '#cbd5e1' }}>—</span>}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedVersionEntry && (
                    <div style={{ marginTop: '14px', padding: '16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', background: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <strong style={{ color: '#1e3a8a' }}>Version details: v{selectedVersionEntry.version}</strong>
                        <span style={{ color: selectedVersionEntry.rejectedAt || ['inactive', 'rejected'].includes(String(selectedVersionEntry.status).toLowerCase()) ? '#dc2626' : '#15803d', fontSize: '11px', fontWeight: '700' }}>
                          {selectedVersionEntry.rejectedAt || ['inactive', 'rejected'].includes(String(selectedVersionEntry.status).toLowerCase()) ? 'Inactive' : 'Active'}
                        </span>
                      </div>
                      {selectedVersionEntry.consentMessage && (
                        <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: selectedVersionEntry.consentMessage }} />
                      )}
                      {selectedVersionEntry.rejectionReason && (
                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#991b1b' }}>Reason: {selectedVersionEntry.rejectionReason}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {versionModalStep === 'reject' && (
                <div>
                  <div style={{ background: 'linear-gradient(135deg, #fff7ed 0%, #fef2f2 100%)', border: '1.5px solid #fed7aa', borderRadius: '14px', padding: '20px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'linear-gradient(135deg, #f97316, #dc2626)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 14px rgba(239,68,68,0.3)' }}>
                      <AlertTriangle size={20} color="#fff" strokeWidth={2.5} />
                    </div>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#7c2d12', marginBottom: '6px' }}>Reject Current Version First</div>
                      <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
                        Before updating to a new version, you must <strong>reject and invalidate</strong> the current version
                        {versionModalCurrentVer && <strong> (v{versionModalCurrentVer})</strong>}.
                      </div>
                    </div>
                  </div>
                  <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Tag size={16} color="#2563eb" />
                      <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>To be rejected</div>
                        <div style={{ fontSize: '20px', fontWeight: '800', color: '#1e3a8a' }}>{versionModalCurrentVer ? `v${versionModalCurrentVer}` : '(none)'}</div>
                      </div>
                    </div>
                    <div style={{ padding: '4px 12px', borderRadius: '999px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <AlertTriangle size={11} />Will be rejected
                    </div>
                  </div>
                </div>
              )}

              {versionModalStep === 'update' && (
                <div>
                  <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1.5px solid #86efac', borderRadius: '14px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircle size={22} color="#16a34a" strokeWidth={2.5} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#14532d' }}>Old version rejected</div>
                      <div style={{ fontSize: '12px', color: '#166534', marginTop: '2px' }}>v{versionModalCurrentVer || '(none)'} has been invalidated. Now set the new version below.</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                      <Tag size={14} color="#2563eb" />New Version *
                    </label>
                    <input
                      type="text" value={pendingNewVersion} onChange={e => setPendingNewVersion(e.target.value)}
                      placeholder="e.g. 2.0, 1.5-beta, 2024.1" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleApplyNewVersion(); }}
                      style={{ width: '100%', padding: '13px 16px', border: '2px solid #bfdbfe', borderRadius: '10px', fontSize: '15px', fontWeight: '700', color: '#1e3a8a', outline: 'none', background: '#eff6ff', boxSizing: 'border-box', transition: 'border-color 0.2s', letterSpacing: '0.02em' }}
                      onFocus={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#fff'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.background = '#eff6ff'; }}
                    />
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>Press Enter to apply.</div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '10px', background: '#fafafa' }}>
              {versionModalStep === 'view' && (
                <>
                  {/* <button onClick={() => setVersionModalStep('reject')} */}
                  <button type="button" onClick={(e) => { e.stopPropagation(); setVersionModalStep('reject'); }}
                    style={{ flex: 1, padding: '12px 20px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 14px rgba(37,99,235,0.3)' }}
                    onMouseDown={e => e.stopPropagation()}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(37,99,235,0.3)'; }}
                  ><Tag size={16} />Update Version</button>
                  <button onClick={handleCloseVersionModal}
                    style={{ padding: '12px 20px', background: '#fff', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                  >Close</button>
                </>
              )}
              {versionModalStep === 'reject' && (
                <>
                  {/* <button onClick={handleRejectOldVersion} disabled={versionRejecting}
                  style={{flex:1, padding:'12px 20px', background: versionRejecting ? '#fca5a5' : 'linear-gradient(135deg, #ef4444, #dc2626)', color:'#fff', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor:'pointer', transition:'all 0.2s', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', boxShadow: '0 4px 14px rgba(220,38,38,0.3)'}} */}
                  <button type="button" onClick={(e) => handleRejectOldVersion(e)} onMouseDown={e => e.stopPropagation()}
                    style={{ flex: 1, padding: '12px 20px', background: versionRejecting ? '#fca5a5' : 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 14px rgba(220,38,38,0.3)' }}
                    onMouseEnter={e => { if (!versionRejecting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(220,38,38,0.4)'; } }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(220,38,38,0.3)'; }}
                  ><AlertTriangle size={16} />{versionRejecting ? 'Rejecting…' : 'Confirm Reject Old Version'}</button>
                  <button onClick={() => setVersionModalStep('view')} disabled={versionRejecting}
                    style={{ padding: '12px 20px', background: '#fff', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  >Back</button>
                </>
              )}
              {versionModalStep === 'update' && (
                <>
                  <button onClick={handleApplyNewVersion}
                    style={{ flex: 1, padding: '12px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 14px rgba(16,185,129,0.3)' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(16,185,129,0.4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(16,185,129,0.3)'; }}
                  ><CheckCircle size={16} />Apply New Version</button>
                  <button onClick={handleCloseVersionModal}
                    style={{ padding: '12px 20px', background: '#fff', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  >Cancel</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {guideOpen && (
        <GuideModal
          steps={GUIDE_STEPS}
          onClose={() => setGuideOpen(false)}
        />
      )}

    </div>
  );
}
export default ConsentPage;